import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useLang, tx } from '../../i18n/LanguageContext';
import { SIM_ICONS, iconFor, errText } from './simUtils';

const inp = 'h-9 rounded-lg border border-slate-200 px-2.5 text-sm outline-none focus:border-blue-500';
const err = (e) => errText(e);

const CatEditor = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  return (
    <div data-testid="sim-cat-editor" className="flex flex-wrap items-center gap-2 py-2">
      <input data-testid="sim-cat-name-ar" value={f.nameAr} onChange={e => setF({ ...f, nameAr: e.target.value })} placeholder="الاسم (عربي)" className={`${inp} w-40`} />
      <input data-testid="sim-cat-name-en" dir="ltr" value={f.nameEn} onChange={e => setF({ ...f, nameEn: e.target.value })} placeholder="Name (English)" className={`${inp} w-40`} />
      <select data-testid="sim-cat-icon" value={f.icon} onChange={e => setF({ ...f, icon: e.target.value })} className={inp}>
        {Object.entries(SIM_ICONS).map(([k, v]) => <option key={k} value={k}>{v.ar}</option>)}
      </select>
      <input data-testid="sim-cat-order" type="number" value={f.order} onChange={e => setF({ ...f, order: Number(e.target.value) })} className={`${inp} w-20`} title="الترتيب" />
      <button data-testid="sim-cat-save" onClick={() => (f.nameAr.trim() ? onSave(f) : toast.error('اكتب اسم التصنيف بالعربية'))} className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50"><Check className="w-4 h-4" /></button>
      <button data-testid="sim-cat-cancel" onClick={onCancel} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><X className="w-4 h-4" /></button>
    </div>
  );
};

export const SimCategoriesPanel = ({ categories, sims, reload }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [editing, setEditing] = useState(null);
  const save = async (f) => {
    const body = { nameAr: f.nameAr, nameEn: f.nameEn, icon: f.icon, order: f.order };
    try {
      if (f.id) await api.put(`/simulation-categories/${f.id}`, body); else await api.post('/simulation-categories', body);
      setEditing(null); reload(); toast.success(L('تم حفظ التصنيف', 'Category saved'));
    } catch (e) { toast.error(err(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm(L('حذف هذا التصنيف؟', 'Delete this category?'))) return;
    try { await api.delete(`/simulation-categories/${c.id}`); reload(); } catch (e) { toast.error(err(e)); }
  };
  return (
    <div data-testid="sim-categories-panel" className="rounded-xl border border-slate-200 p-4 mb-5">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-bold text-slate-900 text-sm">{L('تصنيفات المحاكاة', 'Simulation categories')}</h4>
        <button data-testid="sim-cat-add-btn" onClick={() => setEditing({ nameAr: '', nameEn: '', icon: 'cog', order: categories.length })} className="inline-flex items-center gap-1 text-blue-600 text-sm font-bold"><Plus className="w-4 h-4" />{L('تصنيف جديد', 'New category')}</button>
      </div>
      {editing && !editing.id && <CatEditor initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
      <ul className="divide-y divide-slate-100">
        {categories.map(c => editing?.id === c.id ? <li key={c.id}><CatEditor initial={editing} onSave={save} onCancel={() => setEditing(null)} /></li> : (
          <li key={c.id} data-testid={`sim-cat-row-${c.id}`} className="flex items-center gap-3 py-2 text-sm">
            {React.createElement(iconFor(c.icon).Icon, { className: 'w-4 h-4 text-blue-600' })}
            <span className="flex-1 font-semibold">{c.nameAr} <span className="text-slate-400 font-normal">{c.nameEn}</span></span>
            <span className="text-xs text-slate-400">{sims.filter(s => s.category_id === c.id).length}</span>
            <button data-testid={`sim-cat-edit-${c.id}`} onClick={() => setEditing(c)} className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`sim-cat-delete-${c.id}`} onClick={() => remove(c)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};
